import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  Grid,
  Typography,
  Box,
  Button,
  Modal,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomTextField from "../Utils/CustomTextfield";
import CustomPagination from "../Utils/CustomPagination";
import { useDispatch, useSelector } from "react-redux";
import {
  addDxCode,
  addVisitReason,
  deleteDxcode,
  deleteVisitReason,
  getAllVisitReasons,
  getDxCode,
  updateDxCode,
  updateVisitReason,
} from "../api/action";
import CustomSnackbar from "../Utils/CustomSnackbar";
import DeleteModal from "../Utils/DeleteModal";
import * as XLSX from "xlsx";
import CloseIcon from "@mui/icons-material/Close";
import DXCodeData from "../Data/DXCode";

const DXCodeBody = ({ onEdit, onDelete }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchVisitReasonCode, setSearchVisitReasonCode] = useState("");
  const [searchVisitReasonName, setSearchVisitReasonName] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVisitReason, setSelectedVisitReason] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editVisitReasonCode, setEditVisitReasonCode] = useState("");
  const [editVisitReasonName, setEditVisitReasonName] = useState("");
  const [newVisitReasonCode, setNewVisitReasonCode] = useState("");
  const [newVisitReasonName, setNewVisitReasonName] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState("");
  const [updated, setUpdated] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [id, setId] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const dispatch = useDispatch();
  // const userRole = localStorage.getItem("userRole");
   const userRole = useSelector((state) => state?.user?.userRole)

  useEffect(() => {
    setLoading(true);
    const payload = null;
    dispatch(
      getDxCode(
        payload,
        (data) => {
          setLoading(false);
          setData(data?.data?.Data);
        },
        (error) => {
          console.log(error);
          setLoading(false);
        }
      )
    );
  }, [updated]);

  const handleCheckboxChange = (visitReasonCode) => {
    const isSelected = selectedRows.includes(visitReasonCode);
    setSelectedRows((prevSelected) =>
      isSelected
        ? prevSelected.filter((code) => code !== visitReasonCode)
        : [...prevSelected, visitReasonCode]
    );
  };

  const handleEditClick = (event, selectedVisitReason) => {
    setAnchorEl(event.currentTarget);
    setSelectedVisitReason(selectedVisitReason);
    // Prefill the edit visit reason data
    setEditVisitReasonCode(selectedVisitReason.dx_reason_code);
    setEditVisitReasonName(selectedVisitReason.dx_reason_description);
    setId(selectedVisitReason.id);
    setEditModalOpen(true);
  };

  const handleDelete = () => {
    onDelete(selectedRows);
    setSelectedRows([]);
  };

  useEffect(() => {
    setPage(1);
  }, [searchVisitReasonCode, searchVisitReasonName]);

  const filteredData = data.filter((row) => {
    const matchesVisitReasonCode = row.dx_reason_code
      ?.toLowerCase()
      .includes(searchVisitReasonCode?.toLowerCase());
    const matchesVisitReasonName = row.dx_reason_description
      ?.toLowerCase()
      .includes(searchVisitReasonName?.toLowerCase());
    return matchesVisitReasonCode && matchesVisitReasonName;
  });

  const handleClearSearch = () => {
    setSearchVisitReasonCode("");
    setSearchVisitReasonName("");
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const selectedCodes = data.map((row) => row.dx_reason_code);
      setSelectedRows(selectedCodes);
    } else {
      setSelectedRows([]);
    }
    setSelectAll(event.target.checked);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleAddNewVisitReason = (e) => {
    e.preventDefault();

    if (!newVisitReasonCode || !newVisitReasonName) {
      setLoading(false);
      setSnackbarMessage("Please fill all the fields with valid data.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const payload = {
      dx_reason_description: newVisitReasonName.trim(),
      dx_reason_code: newVisitReasonCode,
    };

    setLoading(true);
    dispatch(
      addDxCode(
        payload,
        (data) => {
          setLoading(false);
          setSnackbarMessage("DX Code added successfully");
          setSnackbarOpen(true);
          setSnackbarSeverity("success");
          setShowModal(false);
          setUpdated((prevState) => !prevState);
          setNewVisitReasonCode("");
          setNewVisitReasonName("");
        },
        (error) => {
          console.log(error);
          setLoading(false);
          const errorMessage =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.error ||
                "Oops! Looks like we need you to log in again to keep things secure!";
          setSnackbarMessage(errorMessage);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      )
    );
  };

  const handleUpdateVisitReason = (e) => {
    e.preventDefault();
    if (!editVisitReasonName || !editVisitReasonCode) {
      setLoading(false);
      setSnackbarMessage("Please fill all the fields with valid data.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const payload = {
      dx_reason_description: editVisitReasonName.trim(),
      dx_reason_code: editVisitReasonCode,
      dxreason_id: id,
    };

    setLoading(true);
    dispatch(
      updateDxCode(
        payload,
        (data) => {
          setLoading(false);
          setSnackbarMessage("DX Code updated successfully");
          setSnackbarOpen(true);
          setSnackbarSeverity("success");
          setEditModalOpen(false);
          setUpdated((prevState) => !prevState);
        },
        (error) => {
          console.log(error);
          setLoading(false);
          const errorMessage =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.detail ||
                "Oops! Looks like we need you to log in again to keep things secure!";
          setSnackbarMessage(errorMessage);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      )
    );
  };

  const handleDeleteVisitReason = (id) => {
    const payload = {
      dxreason_id: id,
    };

    setLoading(true);
    dispatch(
      deleteDxcode(
        payload,
        (data) => {
          setLoading(false);
          setSnackbarMessage("DX Code deleted successfully");
          setSnackbarOpen(true);
          setSnackbarSeverity("success");
          // setEditModalOpen(false);
          setUpdated((prevState) => !prevState);
          setOpenDeleteModal(false);
        },
        (error) => {
          console.log(error);
          setLoading(false);
          const errorMessage =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.detail ||
                "Oops! Looks like we need you to log in again to keep things secure!";
          setSnackbarMessage(errorMessage);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      )
    );
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDeleteModal = (id) => {
    setOpenDeleteModal(true);
    setId(id);
  };

  const downloadSelectedRows = () => {
    // Filter rows based on selectedRows array
    const exportData = filteredData
      .filter((row) => selectedRows.includes(row.dx_reason_code))
      .map((row) => ({
        "DX Code": row.dx_reason_code,
        "DX Description": row.dx_reason_description,
      }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const columnWidths = [
      { wch: 15 }, // Width of Date column
      { wch: 40 }, // Width of Provider column
    ];

    ws["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "SelectedRows");

    // Save the workbook as an Excel file
    XLSX.writeFile(wb, "DX.xlsx");
  };

  return (
    <Box sx={{ px: 10, py: 2 }}>
      <DeleteModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        loading={loading}
        setLoading={setLoading}
        handleDeletefunction={handleDeleteVisitReason}
        id={id}
      />
      <Typography
        variant="h6"
        sx={{ color: "rgba(29, 57, 105, 1)", fontWeight: "bold", mb: 2 }}
      >
        View DX Codes
      </Typography>
      <Grid container spacing={5}>
        <Grid item md={3} xs={6}>
          <InputLabel>Dx Code</InputLabel>
          <CustomTextField
            value={searchVisitReasonCode}
            setValue={setSearchVisitReasonCode}
          />
        </Grid>
        <Grid item md={3} xs={6}>
          <InputLabel>Dx Description</InputLabel>
          <CustomTextField
            value={searchVisitReasonName}
            setValue={setSearchVisitReasonName}
          />
        </Grid>

        <Grid item md={6} xs={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "right",
              alignItems: "right",
              verticalAlign: "middle",
              py: 3,
            }}
          >
            <Button
              className="buttonPadding"
              variant="contained"
              onClick={handleClearSearch}
              sx={{ boxShadow: "none !important", mr: 2 }}
            >
              Clear
            </Button>
            {/* <Button
                            className='buttonPadding'
                            variant="contained"
                            onClick={handleDelete}
                            disabled={selectedRows.length === 0}
                            sx={{ background: selectedRows.length === 0 ? '#d2d2d2' : 'rgba(29, 57, 105, 1) !important', boxShadow: 'none !important', mr: 2 }}
                        >
                            Delete
                        </Button> */}
            <Button
              disabled={userRole !== "Admin"}
              className="buttonPadding"
              variant="contained"
              onClick={() => setShowModal(true)}
              sx={{
                background: userRole !== "Admin" ? "#899499" : null,
                boxShadow: "none !important",
                mr: 2,
              }}
            >
              Add New
            </Button>
            <Button
              disabled={selectedRows.length === 0}
              variant="contained"
              onClick={downloadSelectedRows}
              sx={{
                boxShadow: "none !important",
                background: selectedRows.length === 0 ? "#d2d2d2" : null,
              }}
            >
              Download Excel
            </Button>
          </Box>
        </Grid>
      </Grid>

      {loading ? (
        <Box
          sx={{
            minHeight: "40vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress sx={{ color: "rgba(29, 57, 105, 1) !important" }} />
        </Box>
      ) : filteredData.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "#555", mt: 15 }}
        >
          No Data Available
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} className="scrollBar">
            <Table>
              <TableHead
                sx={{
                  background:
                  "rgba(29, 57, 105, 1)",
                  color: "#fff",
                }}
              >
                <TableRow>
                  <TableCell sx={{ color: "#fff", py: 0.3 }}>
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      style={{ color: "#fff" }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#fff", py: 0.3 }}>DX Code</TableCell>
                  <TableCell sx={{ color: "#fff", py: 0.3 }}>
                    DX Description
                  </TableCell>
                  <TableCell sx={{ color: "#fff", py: 0.3 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.visitReasonCode}>
                      <TableCell sx={{ py: 0.3 }}>
                        <Checkbox
                          size="small"
                          checked={selectedRows.includes(row.dx_reason_code)}
                          onChange={() =>
                            handleCheckboxChange(row.dx_reason_code)
                          }
                          style={{ color: "rgba(29, 57, 105, 1)" }}
                        />
                      </TableCell>
                      <TableCell className="fs12" sx={{ py: 0.3 }}>
                        {row.dx_reason_code}
                      </TableCell>
                      <TableCell className="fs12" sx={{ py: 0.3 }}>
                        {row.dx_reason_description}
                      </TableCell>
                      <TableCell className="fs12" sx={{ py: 0.3 }}>
                        <IconButton
                          disabled={userRole !== "Admin"}
                          color="primary"
                          onClick={(e) => handleEditClick(e, row)}
                        >
                          <EditIcon
                            sx={{
                              color:
                                userRole !== "Admin" ? "#d3d3d3" : "rgba(29, 57, 105, 1)",
                              fontSize: "16px",
                            }}
                          />
                        </IconButton>
                        <IconButton
                          disabled={userRole !== "Admin"}
                          color="secondary"
                          onClick={() => handleDeleteModal(row.id)}
                        >
                          <DeleteIcon
                            sx={{
                              color:
                                userRole !== "Admin" ? "#d3d3d3" : "rgba(29, 57, 105, 1)",
                              fontSize: "16px",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <CustomPagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={handleChangePage}
          />
        </>
      )}

      {/* Modal for adding new visit reason */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Box
          sx={{
            background: "#fff",
            width: "40vw",
            px: 10,
            py: 5,
            mx: "auto",
            borderRadius: "6px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              verticalAlign: "middle",
            }}
          >
            <Typography
              className="fs16"
              sx={{ fontWeight: 600, color: "rgba(29, 57, 105, 1)" }}
            >
              Add New DX Code
            </Typography>
            <IconButton onClick={() => setShowModal(false)}>
              <CloseIcon
                sx={{
                  color: "#fff",
                  background: "rgba(29, 57, 105, 1)",
                  borderRadius: "25px",
                  height: 20,
                  width: 20,
                  p: 0.5,
                }}
              />
            </IconButton>
          </Box>
          <InputLabel sx={{ mt: 2 }}>DX Code</InputLabel>
          <CustomTextField
            value={newVisitReasonCode}
            setValue={setNewVisitReasonCode}
          />
          <InputLabel sx={{ mt: 2 }}>DX Description</InputLabel>
          <CustomTextField
            value={newVisitReasonName}
            setValue={setNewVisitReasonName}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={handleAddNewVisitReason}
              color="primary"
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal for editing  visit reason */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Box
          sx={{
            background: "#fff",
            width: "40vw",
            px: 10,
            py: 5,
            mx: "auto",
            borderRadius: "6px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              verticalAlign: "middle",
            }}
          >
            <Typography
              className="fs16"
              sx={{ fontWeight: 600, color: "rgba(29, 57, 105, 1)" }}
            >
              Edit DX Description
            </Typography>
            <IconButton onClick={() => setEditModalOpen(false)}>
              <CloseIcon
                sx={{
                  color: "#fff",
                  background: "rgba(29, 57, 105, 1)",
                  borderRadius: "25px",
                  height: 20,
                  width: 20,
                  p: 0.5,
                }}
              />
            </IconButton>
          </Box>
          <InputLabel sx={{ mt: 2 }}>DX Code</InputLabel>
          <CustomTextField
            value={editVisitReasonCode}
            setValue={setEditVisitReasonCode}
          />
          <InputLabel sx={{ mt: 2 }}>DX Description</InputLabel>
          <CustomTextField
            value={editVisitReasonName}
            setValue={setEditVisitReasonName}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={handleUpdateVisitReason}
              color="primary"
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                "Update"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      <CustomSnackbar
        open={snackbarOpen}
        handleClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default DXCodeBody;
